import type { RefObject } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import type { PDFViewer } from 'pdfjs-dist/web/pdf_viewer.mjs';

import { getPdfPageView } from './pdfPageView';
import type { PdfPageLayer, PdfPageLayerContext, PdfViewBox } from './types';

type PdfPageLayerCleanups = Map<string, () => void>;

type UsePdfPageLayersOptions = {
  pageLayers: readonly PdfPageLayer[];
  viewerRef: RefObject<PDFViewer | null>;
};

export function cleanupPdfPageLayers(
  cleanups: PdfPageLayerCleanups,
  pageNumber?: number
): void {
  const prefix = pageNumber === undefined ? null : `${pageNumber}:`;
  for (const [key, cleanup] of cleanups) {
    if (prefix === null || key.startsWith(prefix)) {
      cleanup();
      cleanups.delete(key);
    }
  }
}

export function renderPdfPageLayers(
  layers: readonly PdfPageLayer[],
  context: PdfPageLayerContext,
  cleanups: PdfPageLayerCleanups
): void {
  cleanupPdfPageLayers(cleanups, context.pageNumber);
  for (const [index, layer] of layers.entries()) {
    const cleanup = layer(context);
    if (cleanup) {
      cleanups.set(`${context.pageNumber}:${index}`, cleanup);
    }
  }
}

/**
 * Mounts custom overlay layers onto PDF.js page views and keeps them in sync
 * with viewer lifecycle.
 *
 * On each page render (and when `pageLayers` changes), resolves the page's DOM
 * node and viewport from the viewer, then mounts layers. Cleanups from previous
 * mounts are torn down before re-rendering so overlays do not accumulate.
 * `handlePageRendered` also schedules a second pass on the next animation frame
 * so layers attach after PDF.js finishes layout.
 */
export function usePdfPageLayers({
  pageLayers,
  viewerRef,
}: UsePdfPageLayersOptions) {
  const layerCleanupsRef = useRef<PdfPageLayerCleanups>(new Map());
  const pageLayersRef = useRef(pageLayers);
  pageLayersRef.current = pageLayers;

  const cleanupLayers = useCallback(() => {
    cleanupPdfPageLayers(layerCleanupsRef.current);
  }, []);

  const renderLayers = useCallback(
    (pageNumber: number) => {
      const pageView = getPdfPageView(viewerRef.current, pageNumber);
      if (!pageView) {
        return;
      }

      const { div: pageElement, viewport } = pageView;
      const viewBox = viewport.viewBox as PdfViewBox;
      renderPdfPageLayers(
        pageLayersRef.current,
        {
          pageNumber,
          pageElement,
          viewport: {
            width: viewport.width,
            height: viewport.height,
            rotation: viewport.rotation,
            viewBox,
            convertToViewportRectangle: (rect) => {
              const [x1, y1] = viewport.convertToViewportPoint(
                rect[0],
                rect[1]
              );
              const [x2, y2] = viewport.convertToViewportPoint(
                rect[2],
                rect[3]
              );
              return [x1, y1, x2, y2];
            },
          },
        },
        layerCleanupsRef.current
      );
    },
    [viewerRef]
  );

  const handlePageRendered = useCallback(
    (pageNumber: number) => {
      renderLayers(pageNumber);
      requestAnimationFrame(() => renderLayers(pageNumber));
    },
    [renderLayers]
  );

  useEffect(() => {
    cleanupLayers();
    const pageCount = viewerRef.current?.pagesCount ?? 0;
    for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
      renderLayers(pageNumber);
    }
  }, [cleanupLayers, pageLayers, renderLayers, viewerRef]);

  return { cleanupLayers, handlePageRendered };
}
